import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Feedback } from '../../../config/api/models/Feedback';
import { useStudentsByGroup, useUser } from '../../shared';
import { TabbedContent, TabbedContentData } from '../../shared/components';
import { useStudentFeedbackForTrajectory, useTeacherFeedback } from '../hooks';
import { FeedbackModuleContext, FeedbackModuleMode } from '../types';
import { StyledCollapse } from './Atoms';
import { Panel } from './Panel';
import { ExistingFeedback } from './existing-feedback';
import { NewFeedback } from './new-feedback';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';

type FeedbackModuleProps = {
  pageId: string;
  blockId?: string;
  context: FeedbackModuleContext;
  mode: FeedbackModuleMode;
  open?: boolean;
  onClose?: () => void;
};

export const FeedbackModule: FC<FeedbackModuleProps> = ({
  pageId,
  blockId,
  context,
  mode,
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const { isTeacher, isStudent } = useUser();
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const { data: students } = useStudentsByGroup(selectedGroup || '', isTeacher && open && selectedGroup !== undefined && selectedGroup !== '');
  const { feedback: teacherFeedback, refetch: refetchTeacherFeedback } = useTeacherFeedback();

  const { feedback: studentFeedback, refetch: refetchStudentFeedback } =
    useStudentFeedbackForTrajectory();

  const relevantTeacherFeedback: Feedback[] | undefined = useMemo(() => {
    if (teacherFeedback != null && teacherFeedback.some(item => item.pageId === pageId)) {
      return blockId === undefined
        ? teacherFeedback.find(item => item.pageId === pageId)?.trajectorFeedbacks
        : teacherFeedback.find(item => item.pageId === pageId)?.blockFeedbacks?.find(item => item.blockId === blockId)?.feedbacks;
    }
  }, [teacherFeedback, pageId, blockId]);

  const relevantStudentFeedback: Feedback[] | undefined = useMemo(() => {
    if (studentFeedback != null && studentFeedback.some(item => item.pageId === pageId)) {
      return blockId === undefined
        ? studentFeedback.find(item => item.pageId === pageId)?.feedback?.trajectoryFeedbacks
        : studentFeedback.find(item => item.pageId === pageId)?.feedback?.blockFeedbacks.filter(item => item.blockId === blockId);
    }
  }, [studentFeedback, pageId, blockId]);

  const handleUpdateTeacher = useCallback(() => {
    refetchTeacherFeedback();
    setSelectedTab(0);
  }, [refetchTeacherFeedback]);

  const handleUpdateStudent = useCallback(() => {
    refetchStudentFeedback();
    setSelectedTab(0);
  }, [refetchStudentFeedback]);

  const tabbedContent: TabbedContentData[] = [
    ...(relevantTeacherFeedback != null && relevantTeacherFeedback.length > 0
      ? [
          {
            title: t('feedback.existingFeedback'),
            data: (
              <ExistingFeedback
                selectedGroupData={students?.data || []}
                data={relevantTeacherFeedback}
                onUpdate={handleUpdateTeacher}
              />
            ),
          },
        ]
      : []),
    ...(relevantStudentFeedback != null && relevantStudentFeedback.length > 0
      ? [
          {
            title: t('feedback.existingFeedback'),
            data: (
              <ExistingFeedback
                data={relevantStudentFeedback}
                onUpdate={handleUpdateStudent}
              />
            ),
          },
        ]
      : []),
    ...(isTeacher
      ? [
          {
            title: t('feedback.newFeedback'),
            data: (
              <NewFeedback
                pageId={pageId}
                blockId={blockId}
                onSuccess={handleUpdateTeacher}
                selectedGroupData={students?.data || []}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <StyledCollapse
      in={mode === 'embedded' ? true : open}
      embedded={mode === 'embedded'}
      appear
      mountOnEnter
      unmountOnExit
    >
      <Panel
        id={`feedback_${blockId || pageId}`}
        embedded={mode === 'embedded'}
        context={context}
        onClose={onClose}
      >
        <TabbedContent
          ariaPrefix="feedback"
          content={tabbedContent}
          onChange={setSelectedTab}
          value={selectedTab}
          hideTabs={isStudent}
        />
      </Panel>
    </StyledCollapse>
  );
};
